import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { surveysService } from "../services/surveys";

export default function SurveyAnalyticsPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["analytics", id],
    queryFn: () => surveysService.getAnalytics(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="text-red-400">Error loading analytics: {(error as Error).message}</p>
      </div>
    );
  }

  const analytics = data;

  if (!analytics) {
    return (
      <div className="card">
        <p className="text-gray-400">Analytics not available.</p>
      </div>
    );
  }

  const survey = analytics.survey as { title: string; status: string; target_responses: number; actual_responses: number; completion_rate: string };
  const questions = analytics.questions as Array<{
    id: string;
    question_text: string;
    total_responses: number;
    options: Array<{
      id: string;
      option_text: string;
      response_count: number;
      percentage: string;
    }>;
  }>;
  const participations = analytics.participations as { total: string };
  const responsesByDate = analytics.responses_by_date as Array<{ date: string; count: string }>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-100">Analytics: {survey.title}</h1>
        <Link to={`/surveys/${id}`} className="btn btn-secondary">
          Back to Survey
        </Link>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Status</h3>
          <p className="text-xl font-bold capitalize text-gray-100">{survey.status}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Completion Rate</h3>
          <p className="text-xl font-bold text-primary-300">{survey.completion_rate}%</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Target Responses</h3>
          <p className="text-xl font-bold text-gray-200">{survey.target_responses}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Actual Responses</h3>
          <p className="text-xl font-bold text-green-400">{survey.actual_responses}</p>
        </div>
      </div>

      {/* Question Breakdown */}
      <div className="space-y-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-100">Question Breakdown</h2>
        {questions.map((question, qIndex) => (
          <div key={question.id} className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-100">
                {qIndex + 1}. {question.question_text}
              </h3>
              <span className="text-sm text-gray-500">{question.total_responses} responses</span>
            </div>

            {/* Options with percentage bars */}
            <div className="space-y-3">
              {question.options.map((option) => (
                <div key={option.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-200">{option.option_text}</span>
                    <span className="text-gray-400">
                      {option.response_count} ({option.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-dark-800 rounded-full h-3">
                    <div
                      className="bg-primary-500 h-3 rounded-full transition-all"
                      style={{ width: `${option.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Responses Over Time */}
      {responsesByDate.length > 0 && (
        <div className="card mb-8">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Responses Over Time (Last 7 Days)</h2>
          <div className="space-y-2">
            {responsesByDate.map((item) => (
              <div key={item.date} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  {new Date(item.date).toLocaleDateString()}
                </span>
                <span className="font-medium text-gray-200">{item.count} responses</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Participation Stats */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Participation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-400">Total Invitations Sent</p>
            <p className="text-2xl font-bold text-gray-100">{participations.total}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Response Rate</p>
            <p className="text-2xl font-bold text-green-400">
              {participations.total && parseInt(participations.total) > 0
                ? ((survey.actual_responses / parseInt(participations.total)) * 100).toFixed(1)
                : "0"}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
